import React, { useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/dialog';
import { Button } from '../../components/button';
import { X, Download, Eye } from 'lucide-react';

export interface ViewerItem {
    id: string;
    url: string;
    title?: string;
    alt?: string;
    type: 'image' | 'document' | 'video' | 'file';
    downloadUrl?: string;
}

interface PhotoDocumentViewerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    items: ViewerItem[];
    title?: string;
}

/**
 * Reusable modal component for viewing photos and documents
 * Displays all items in a single column grid layout
 */
export const PhotoDocumentViewerModal = ({
    open,
    onOpenChange,
    items,
    title = 'Viewer',
}: PhotoDocumentViewerModalProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const handleDownload = (item: ViewerItem) => {
        if (item.downloadUrl || item.url) {
            const link = document.createElement('a');
            link.href = item.downloadUrl || item.url;
            link.download = item.title || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!open) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            onOpenChange(false);
        }
    };

    if (items.length === 0) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex-col overflow-hidden p-0"
                onKeyDown={handleKeyDown}
                closeButton={false}
                variant="fullScreen"
            >
                <DialogHeader className="relative max-h-10 w-full">
                    <DialogTitle className="sr-only">{title}</DialogTitle>
                    <Button
                        color="transparent"
                        size={'square36'}
                        onClick={() => onOpenChange(false)}
                        className="absolute right-4 top-4"
                    >
                        <X className="h-10 w-10" />
                    </Button>
                </DialogHeader>

                <div
                    className="relative min-h-0 flex-1 overflow-auto"
                    ref={containerRef}
                >
                    {/* Content area - Single column grid */}
                    <div className="flex flex-col gap-6 px-6 pb-6 pt-12">
                        {items.map((item, index) => (
                            <div
                                key={item.id}
                                className="flex flex-col items-center"
                            >
                                {item.type === 'image' ? (
                                    <div className="relative w-full px-12">
                                        <div
                                            className="aspect-video w-full overflow-hidden rounded-lg"
                                            style={{
                                                height: 'calc(100dvh - 10rem)',
                                            }}
                                        >
                                            <img
                                                src={item.url}
                                                alt={
                                                    item.alt ||
                                                    item.title ||
                                                    `Image ${index + 1}`
                                                }
                                                className="h-full w-full object-contain"
                                                loading="eager"
                                            />
                                        </div>
                                    </div>
                                ) : item.type === 'video' ? (
                                    <div className="relative w-full px-12">
                                        <div
                                            className="mx-auto w-fit overflow-hidden rounded-lg"
                                            style={{
                                                height: 'calc(100dvh - 10rem)',
                                            }}
                                        >
                                            <video
                                                src={item.url}
                                                controls
                                                className="h-full"
                                                preload="metadata"
                                            >
                                                Your browser does not support
                                                the video tag.
                                            </video>
                                        </div>
                                    </div>
                                ) : item.type === 'document' ? (
                                    <div className="relative w-full">
                                        <iframe
                                            src={`${item.url}#navpanes=0&scrollbar=0`}
                                            className="h-[90dvh] w-full"
                                            title={item.title || 'Document'}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex max-w-md flex-col items-center justify-center gap-4 text-center">
                                        <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-muted">
                                            <Eye className="h-12 w-12 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <h3 className="mb-2 text-lg font-semibold">
                                                {item.title || 'File'}
                                            </h3>
                                            <p className="mb-4 text-muted-foreground">
                                                This file cannot be previewed in
                                                the browser.
                                            </p>
                                            <Button
                                                onClick={() =>
                                                    handleDownload(item)
                                                }
                                                className="gap-2"
                                            >
                                                <Download className="h-4 w-4" />
                                                Download File
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
